import { Component } from '@angular/core';
import { Alert } from 'src/app/shared/models/Alert';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ValidatePermissionsService } from 'src/app/shared/services/validate-permissions.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { emailValidator } from 'src/app/validators/email.validator';
import { InventoryService } from 'src/app/services/inventory.service';
import { RatePlanDTO } from 'src/app/models/RatePlanDTO.model';
import { FileDTO } from 'src/app/models/FileDTO.model';
import { DeviceDTO } from 'src/app/models/DeviceDTO.model';
import { MasterdataRequestDTO } from 'src/app/models/MasterdataRequestDTO.model';
import { MasterDataDTO } from 'src/app/models/MasterDataDTO.model';
import { CarrierDTO } from 'src/app/models/CarrierDTO.model';
import { carrierConstants } from 'src/app/shared/constants/carrierConstants';
import { ResponseBase } from 'src/app/models/ResponseBase';

import { RouterModule } from '@angular/router';
import { DeviceFormComponent } from '../../devices/device-form/device-form.component';
@Component({
    selector: 'app-inventory-create',
    templateUrl: './inventory-create.component.html',
    styleUrl: './inventory-create.component.css',
    imports: [RouterModule, DeviceFormComponent]
})
export class InventoryCreateComponent {
  form = new FormGroup({
    email: new FormControl('', [Validators.required, emailValidator()]),
  });

  get emailModel() {
    return this.form.get('email');
  }

  selectedFile: File | null = null;
  fileError: boolean = false;
  fieldImportCompleted: boolean = false;
  iccid: string = '';
  zipCode: string = '';
  imei: string = '';
  firstName: string = '';
  lastName: string = '';
  costCenter: string = '';
  information: string = 'Create Device';
  informationFile: string = "Import SIM's (CSV)";
  fieldsCompleted: boolean = false;
  ratePlans: RatePlanDTO[] = {} as RatePlanDTO[];
  filteredRatePlans: RatePlanDTO[] = [];
  isZipCodeEnabled: boolean = false;
  originatedFrom: string = '';

  carriers: CarrierDTO[] = {} as CarrierDTO[];
  newSim: DeviceDTO = {} as DeviceDTO;
  simsAvailables: DeviceDTO = {} as DeviceDTO;
  fileDTO: FileDTO = {} as FileDTO;
  ratePlanSelected: string = '';
  carrierSelected: string = '';
  assignedSelected: string = '';
  masterdataRequestDTO: MasterdataRequestDTO = {
    nameMaster: 'INVENTORY_ASSIGNED',
  };
  listAssignedTo: MasterDataDTO[] = [];

  alert: Alert = {} as Alert;

  constructor(
    private alertService: AlertService,
    public permissionsService: ValidatePermissionsService,
    public inventoryService: InventoryService
  ) { }

  ngOnInit() {
    this.permissionsService.validateAccessLevels(MODULE_ID.Inventory);
    const now = new Date();

    this.inventoryService.getCarriers().subscribe(
      ({ StatusCode, Message, Data }) =>
        Data != null
          ? this.handleGetCarriers(Data)
          : this.handleError(StatusCode),
      (error) => {
        this.clearFields();
      }
    );

    this.inventoryService.getRatePlans().subscribe(
      ({ StatusCode, Message, Data }) =>
        Data != null
          ? this.handleGetAllRatePlansSuccess(Data)
          : this.handleError(StatusCode),
      (error) => {
        this.alert = {
          alertMessage: 'Unexpected error. Please try again later.',
          alertType: 'alert alert-danger',
          alertCenter: false,
          alerTitle: '',
          duration: 3000,
        };
        this.alertService.updateAlertState(this.alert);
        this.clearFields();
      }
    );

    this.inventoryService.getMastersByName(this.masterdataRequestDTO).subscribe(
      ({ StatusCode, Message, Data }) =>
        Data != null
          ? this.handleGetMastersById(Data)
          : this.handleError(StatusCode),
      (error) => {
        this.clearFields();
      }
    );
  }

  public get carrierConstants() {
    return carrierConstants;
  }

  handleGetMastersById(data: MasterDataDTO[]) {
    this.listAssignedTo = data;
  }

  handleGetCarriers(data: CarrierDTO[]) {
    this.carriers = data;
  }

  goBack() {
    window.location.href = '/home';
  }

  createSim() {
    this.newSim.iccid = this.iccid.trim();
    this.newSim.ratePlanId = Number(this.ratePlanSelected);
    this.newSim.imei = this.imei;
    this.newSim.firstName = this.firstName;
    this.newSim.lastName = this.lastName;
    this.newSim.costCode = this.costCenter;
    this.newSim.idAssignedTo = this.assignedSelected;
    this.newSim.carrierId = this.getCarrierNameById(this.carrierSelected); // Agregando ID del Carrier
    this.newSim.zipCode = this.zipCode; // Agregando Zip Code
    this.newSim.originatedFrom = this.originatedFrom;

    this.inventoryService.createSim(this.newSim).subscribe(
      (response: ResponseBase) => {
        this.handleSaveSuccess(response)
      },


      (error) => {
        this.alert = {
          alertMessage: 'Unexpected error. Please try again later.',
          alertType: 'alert alert-danger',
          alertCenter: false,
          alerTitle: '',
          duration: 3000,
        };
        this.alertService.updateAlertState(this.alert);
        this.clearFields();
        this.clearFields();
      }
    );
  }

  getCarrierNameById(carrierId: string): string {
    const carrier = this.carriers.find(
      (c) => c.aCarrierId === Number(carrierId)
    );
    return carrier?.aCarrierName ?? ''; // Si es undefined, devuelve ""
  }

  // Método para filtrar Rate Plans según el Carrier seleccionado y habilitar Zip Code
  filterRatePlans() {
    if (this.carrierSelected) {
      this.filteredRatePlans = this.ratePlans.filter(
        (plan) => plan.idCarrier === Number(this.carrierSelected)
      );
    } else {
      this.filteredRatePlans = [];
    }

    // Limpiar la selección de Rate Plan si no está en la lista filtrada
    if (
      !this.filteredRatePlans.some(
        (plan) => plan.ratePlanId === Number(this.ratePlanSelected)
      )
    ) {
      this.ratePlanSelected = '';
    }

    // Habilitar el campo Zip Code solo si el Carrier seleccionado es 1
    this.isZipCodeEnabled = this.carrierSelected === '1';

    // Si se deshabilita, limpiar el campo Zip Code
    if (!this.isZipCodeEnabled) {
      this.zipCode = '';
    }
  }

  handleError(StatusCode: number) {
    this.clearFields();
  }

  async handleSaveSuccess(response: ResponseBase) {
    if (response.hasValidationErrors) {
       this.alertService.error(response.validationErrorMessage);
    } else {
     
      this.alertService.success('Device created successfully');

      this.clearFields();
      location.reload();
    }
  }


  handleGetAllRatePlansSuccess(ratePlans: RatePlanDTO[]) {
    this.ratePlans = ratePlans;
  }

  clearFields() { }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validExtensions = ['csv', 'xlsx', 'xls'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (fileExtension && validExtensions.includes(fileExtension)) {
        this.selectedFile = file;
        this.fileError = false;
        this.fieldImportCompleted = true; // Habilitar el botón
      } else {
        this.selectedFile = null;
        this.fileError = true;
        this.fieldImportCompleted = false;
      }
    }
  }

  shouldShowZipCodeAsterisk(): boolean {
    return this.isZipCodeEnabled;
  }

  checkFieldsCarrier() {

    this.iccid = '';

    this.filterRatePlans();
    this.validateFields();
  }

  checkFieldIccid() {


    if (this.iccid?.startsWith('8901')) {
      var a = this.carriers.find(a => a.aCarrierName == this.carrierConstants['at&t'])
      this.carrierSelected = a?.aCarrierId?.toString() ?? "";
    }
    else if (this.iccid?.startsWith('8914')) {
      var a = this.carriers.find(a => a.aCarrierName == this.carrierConstants.vzw)
      this.carrierSelected = a?.aCarrierId?.toString() ?? "";
    }
    else if (this.iccid?.startsWith('8985')) {
      var a = this.carriers.find(a => a.aCarrierName == this.carrierConstants.msim)
      this.carrierSelected = a?.aCarrierId?.toString() ?? "";
    }
    else {
      this.carrierSelected = "";
    }


    this.filterRatePlans();
    this.validateFields();
  }

  checkFields() {
    this.validateFields();
  }

  checkFieldsImport() {
    if (this.iccid) {
      this.fieldsCompleted = true;
    } else {
      this.fieldsCompleted = false;
    }
  }

  validateFields() {
    if (this.iccid && this.carrierSelected && this.ratePlanSelected) {
      // Si el campo Zip Code está habilitado, debe ser obligatorio
      if (this.isZipCodeEnabled && !this.zipCode) {
        this.fieldsCompleted = false;
        return;
      }
      this.fieldsCompleted = true;
    } else {
      this.fieldsCompleted = false;
    }
  }

  downloadTemplate(value: string = '') {
    debugger;
    const rows = [
      ['Carrier', 'SIM', 'Status', 'Assigned_to', 'Date_assigned', 'Current_rate_plan'],
      ['VZW', '891455555555', 'DEACTIVATED', 'Inventory-Shellie', new Date().toLocaleDateString(), 'MACHINE TO MACHINE 250MB ACCT SHR DYNAMIC',],
    ];

    let csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", value == 'update' ? "InventoryUpdateTemplate.csv" : "InventoryCreateTemplate.csv");
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  }
}
